import { useState } from 'react'
import type {
  AdSlotType,
  AdUnitDef,
  AdsCatalog,
  DeviceHeight,
  DeviceSizes,
  ServiceConfig,
  Size,
  SlotBid,
  SlotBlueprint,
} from '@/lib/types'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ArrowDown, ArrowUp, ChevronRight, Trash2 } from 'lucide-react'
import { Field, SizesInput } from '../components/fields'
import { Triple, TypeCode } from '../components/cells'
import { JsonInline } from '../components/json'
import { HIDDEN_TYPES } from './TypesEditor'
import {
  type BlueprintIssues,
  blueprintIssueLabels,
  blueprintIssues,
  hasBlueprintIssues,
} from '../lib/blueprint'

export function SlotsEditor({
  catalog,
  onChangeCatalog,
  draft,
  setDraft,
  onGoToTypes,
}: {
  catalog: AdsCatalog
  onChangeCatalog: (next: AdsCatalog) => void
  draft: ServiceConfig
  setDraft: (cfg: ServiceConfig) => void
  onGoToTypes: () => void
}) {
  const [newSlotName, setNewSlotName] = useState('')
  const [newTypeId, setNewTypeId] = useState<string>(catalog.types[0]?.id ?? 'XL')
  const [filterType, setFilterType] = useState<string>('ALL')

  const typeMap = new Map<string, AdSlotType>(catalog.types.map((t) => [t.id, t]))
  const bpMap = new Map<string, SlotBlueprint>(
    (draft.slotBlueprints ?? []).map((bp) => [bp.slot, bp]),
  )

  function syncDraftBlueprints(nextCatalog: AdsCatalog, nextBlueprints?: SlotBlueprint[]) {
    const bps = nextBlueprints ?? draft.slotBlueprints ?? []
    const map = new Map(bps.map((b) => [b.slot, b]))
    const tMap = new Map(nextCatalog.types.map((t) => [t.id, t]))

    // Domyślne bidy per typ w bieżącym serwisie
    const defaultBidsByType = new Map<string, SlotBid[]>()
    for (const b of bps) {
      if (b.typeId && !defaultBidsByType.has(b.typeId) && b.bids && b.bids.length > 0) {
        defaultBidsByType.set(b.typeId, b.bids)
      }
    }

    const synced: SlotBlueprint[] = nextCatalog.adUnits.map((u) => {
      const prev = map.get(u.slot)
      const t = tMap.get(u.typeId)
      const gamOnly = u.gamOnly || (prev ? !!prev.gamOnly : false)
      const bids =
        prev?.bids && prev.bids.length > 0
          ? prev.bids
          : gamOnly
            ? []
            : (defaultBidsByType.get(u.typeId) ?? [])

      return {
        slot: u.slot,
        typeId: u.typeId,
        // `active` jest per serwis — katalog daje tylko wartość startową dla nowego adUnitu.
        active: prev?.active ?? u.active,
        sizes: t ? { ...t.sizes, ...u.sizes } : (prev?.sizes ?? { desktop: [], tablet: [], mobile: [] }),
        adHigh: u.adHigh,
        gamOnly,
        bids,
      }
    })
    setDraft({ ...draft, slotBlueprints: synced })
  }

  function addAdUnit() {
    const slot = newSlotName.trim()
    if (!slot || catalog.adUnits.some((u) => u.slot === slot)) return
    const selectedType = typeMap.get(newTypeId) ?? catalog.types[0]
    if (!selectedType) return

    const nextUnit: AdUnitDef = {
      slot,
      typeId: selectedType.id,
      adHigh: { ...selectedType.adHigh },
      active: true,
      gamOnly: false,
    }
    const nextCatalog: AdsCatalog = {
      ...catalog,
      adUnits: [...catalog.adUnits, nextUnit],
    }
    onChangeCatalog(nextCatalog)
    syncDraftBlueprints(nextCatalog)
    setNewSlotName('')
  }

  function updateUnit(idx: number, nextUnit: AdUnitDef) {
    const nextCatalog: AdsCatalog = {
      ...catalog,
      adUnits: catalog.adUnits.map((u, i) => (i === idx ? nextUnit : u)),
    }
    onChangeCatalog(nextCatalog)
    syncDraftBlueprints(nextCatalog)
  }

  function removeUnit(idx: number) {
    const target = catalog.adUnits[idx]
    if (!target) return
    if (
      !window.confirm(`Usunąć adUnit „${target.slot}” ze wszystkich aplikacji (SW, RB, BB, AB)?`)
    ) {
      return
    }
    const nextCatalog: AdsCatalog = {
      ...catalog,
      adUnits: catalog.adUnits.filter((_, i) => i !== idx),
    }
    onChangeCatalog(nextCatalog)
    syncDraftBlueprints(nextCatalog)
  }

  function moveUnit(idx: number, dir: -1 | 1) {
    const target = idx + dir
    if (target < 0 || target >= catalog.adUnits.length) return
    const next = catalog.adUnits.slice()
    const tmp = next[idx]!
    next[idx] = next[target]!
    next[target] = tmp
    const nextCatalog: AdsCatalog = { ...catalog, adUnits: next }
    onChangeCatalog(nextCatalog)
    syncDraftBlueprints(nextCatalog)
  }

  function updateBlueprintInService(slot: string, patch: Partial<SlotBlueprint>) {
    const nextBps = (draft.slotBlueprints ?? []).map((b) =>
      b.slot === slot ? { ...b, ...patch } : b,
    )
    setDraft({ ...draft, slotBlueprints: nextBps })
  }

  function copyBidsToSameType(typeId: string, bids: SlotBid[]) {
    const nextBps = (draft.slotBlueprints ?? []).map((b) =>
      b.typeId === typeId && !b.gamOnly
        ? {
            ...b,
            bids: bids.map((bid) => ({
              ...bid,
              params: bid.params ? { ...bid.params } : undefined,
            })),
          }
        : b,
    )
    setDraft({ ...draft, slotBlueprints: nextBps })
  }

  const shownTypes = catalog.types.filter((t) => !HIDDEN_TYPES.has(t.id))
  const shownUnits = catalog.adUnits.filter((u) => !HIDDEN_TYPES.has(u.typeId))
  const problems = (draft.slotBlueprints ?? [])
    .filter((bp) => !HIDDEN_TYPES.has(bp.typeId ?? ''))
    .map((bp) => ({ bp, issues: blueprintIssues(bp, draft) }))
    .filter((x) => hasBlueprintIssues(x.issues))

  const visibleUnits = catalog.adUnits
    .map((unit, idx) => ({ unit, idx }))
    .filter(({ unit }) => !HIDDEN_TYPES.has(unit.typeId))
    .filter(({ unit }) => filterType === 'ALL' || unit.typeId === filterType)

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-700">
              Wspólna lista AdUnitów
            </span>
            <Badge variant="secondary">Równoległa edycja: SW · RB · BB · AB</Badge>
          </div>
          <p className="max-w-2xl text-xs text-slate-500">
            Dodanie AdUnitu, zmiana jego Typu lub wysokości placeholdera (<code>adHigh</code>)
            działa od razu we wszystkich aplikacjach. Rozmiary pochodzą z wybranego Typu i służą
            zarówno w GAM (<code>&lt;AdSlot&gt;</code>), jak i w aukcji Prebid.
          </p>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <Field label="Kod AdUnitu (np. S_DM_HH_L13)">
            <input
              value={newSlotName}
              onChange={(e) => setNewSlotName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addAdUnit()}
              placeholder="np. S_DM_HH_L13"
              className="w-52 rounded border border-slate-200 bg-white px-2 py-1 font-mono text-sm outline-none focus:border-slate-400"
            />
          </Field>
          <Field label="Typ reklamy">
            <select
              value={newTypeId}
              onChange={(e) => setNewTypeId(e.target.value)}
              className="rounded border border-slate-200 bg-white px-2 py-1 font-mono text-sm outline-none focus:border-slate-400"
            >
              {shownTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.id} — {t.name}
                </option>
              ))}
            </select>
          </Field>
          <Button
            type="button"
            size="sm"
            onClick={addAdUnit}
            disabled={
              !newSlotName.trim() || catalog.adUnits.some((u) => u.slot === newSlotName.trim())
            }
          >
            + Dodaj AdUnit
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1">
          <button
            type="button"
            onClick={() => setFilterType('ALL')}
            className={
              'rounded border px-2.5 py-1 text-xs font-medium ' +
              (filterType === 'ALL'
                ? 'border-slate-900 bg-slate-900 text-white'
                : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50')
            }
          >
            Wszystkie ({shownUnits.length})
          </button>
          {shownTypes.map((t) => {
            const count = catalog.adUnits.filter((u) => u.typeId === t.id).length
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setFilterType(t.id)}
                className={
                  'rounded border px-2.5 py-1 font-mono text-xs font-medium ' +
                  (filterType === t.id
                    ? 'border-slate-900 bg-slate-900 text-white'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50')
                }
              >
                {t.id} ({count})
              </button>
            )
          })}
        </div>
        <Button type="button" variant="outline" size="sm" onClick={onGoToTypes}>
          Edytuj Typy i Rozmiary →
        </Button>
      </div>

      {problems.length > 0 && (
        <Alert variant="warning">
          <AlertTitle className="text-xs font-semibold">
            ⚠ {problems.length} {problems.length === 1 ? 'slot może' : 'slotów może'} nie dawać
            bidów
          </AlertTitle>
          <AlertDescription className="text-xs text-amber-900">
            <ul className="list-disc space-y-0.5 pl-4">
              {problems.map(({ bp, issues }) => (
                <li key={bp.slot || Math.random()}>
                  <code className="font-mono font-semibold">{bp.slot || '(brak nazwy)'}</code> —{' '}
                  {blueprintIssueLabels(issues).join(' · ')}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <div className="overflow-hidden rounded-lg border border-slate-200">
        <Table>
          <TableHeader className="bg-slate-50 text-xs uppercase tracking-wide">
            <TableRow className="hover:bg-slate-50">
              <TableHead className="w-10" />
              <TableHead className="text-slate-500">AdUnit</TableHead>
              <TableHead className="text-slate-500">Typ</TableHead>
              <TableHead className="text-slate-500">Placeholder D · T · M</TableHead>
              <TableHead className="text-slate-500">Status ({draft.service})</TableHead>
              <TableHead className="text-slate-500">Prebid ({draft.service})</TableHead>
              <TableHead className="w-32" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleUnits.map(({ unit, idx }) => {
              const type = typeMap.get(unit.typeId)
              const bp: SlotBlueprint = bpMap.get(unit.slot) ?? {
                slot: unit.slot,
                typeId: unit.typeId,
                active: unit.active,
                sizes: type ? { ...type.sizes, ...unit.sizes } : { desktop: [], tablet: [], mobile: [] },
                adHigh: unit.adHigh,
                gamOnly: unit.gamOnly,
                bids: [],
              }
              return (
                <SlotRow
                  key={`${unit.slot}-${idx}`}
                  unit={unit}
                  bp={bp}
                  types={catalog.types}
                  draft={draft}
                  issues={blueprintIssues(bp, draft)}
                  onChangeUnit={(next) => updateUnit(idx, next)}
                  onChangeServiceBp={(patch) => updateBlueprintInService(unit.slot, patch)}
                  onCopyBidsToType={(bids) => copyBidsToSameType(unit.typeId, bids)}
                  onRemove={() => removeUnit(idx)}
                  onMoveUp={idx === 0 ? null : () => moveUnit(idx, -1)}
                  onMoveDown={idx === catalog.adUnits.length - 1 ? null : () => moveUnit(idx, 1)}
                />
              )
            })}
            {visibleUnits.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-6 text-center text-xs text-slate-400">
                  Brak adUnitów.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

function SlotRow({
  unit,
  bp,
  types,
  draft,
  issues,
  onChangeUnit,
  onChangeServiceBp,
  onCopyBidsToType,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  unit: AdUnitDef
  bp: SlotBlueprint
  types: AdSlotType[]
  draft: ServiceConfig
  issues: BlueprintIssues
  onChangeUnit: (next: AdUnitDef) => void
  onChangeServiceBp: (patch: Partial<SlotBlueprint>) => void
  onCopyBidsToType: (bids: SlotBid[]) => void
  onRemove: () => void
  onMoveUp: (() => void) | null
  onMoveDown: (() => void) | null
}) {
  const [open, setOpen] = useState(false)
  const warn = hasBlueprintIssues(issues)
  const bids = bp.bids ?? []
  const gamOnly = unit.gamOnly || !!bp.gamOnly
  const active = bp.active !== false
  const fullGamPath = `${draft.gpidNetwork}/${draft.gpidAccount || draft.service}//${unit.slot}`

  function setDeviceHigh(device: keyof DeviceHeight, value: number) {
    onChangeUnit({
      ...unit,
      adHigh: { ...unit.adHigh, [device]: value },
    })
  }

  const type = types.find((t) => t.id === unit.typeId)

  // `null` = wróć do rozmiarów z typu na tym urządzeniu.
  function setDeviceSizes(device: keyof DeviceSizes, value: Size[] | null) {
    const { [device]: _omit, ...rest } = unit.sizes ?? {}
    const next = value ? { ...rest, [device]: value } : rest
    onChangeUnit({ ...unit, sizes: Object.keys(next).length ? next : undefined })
  }

  function changeType(nextTypeId: string) {
    const nextType = types.find((t) => t.id === nextTypeId)
    onChangeUnit({
      ...unit,
      typeId: nextTypeId,
      adHigh: nextType ? { ...nextType.adHigh } : unit.adHigh,
      sizes: undefined,
    })
  }

  const stop = (fn: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation()
    fn()
  }

  return (
    <>
      <TableRow
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={
          'cursor-pointer ' +
          (open ? 'border-b-0 bg-slate-50 ' : '') +
          (!active ? 'opacity-60 ' : '') +
          (warn ? 'bg-amber-50/60 hover:bg-amber-50' : '')
        }
      >
        <TableCell className="text-slate-400">
          <ChevronRight className={'size-4 transition-transform ' + (open ? 'rotate-90' : '')} />
        </TableCell>
        <TableCell>
          <div className="flex flex-col">
            <code className="font-mono text-sm font-semibold text-slate-800">{unit.slot}</code>
            <span className="font-mono text-[11px] text-slate-400">{fullGamPath}</span>
          </div>
        </TableCell>
        <TableCell>
          <TypeCode id={unit.typeId} />
        </TableCell>
        <TableCell>
          <Triple
            values={[unit.adHigh.desktop, unit.adHigh.tablet, unit.adHigh.mobile]}
            unit="px"
          />
        </TableCell>
        <TableCell>
          <div className="flex flex-wrap items-center gap-1">
            {active ? (
              <Badge
                variant="outline"
                className="border-emerald-200 bg-emerald-50 text-emerald-700"
              >
                aktywny
              </Badge>
            ) : (
              <Badge variant="destructive">nieaktywny</Badge>
            )}
            {unit.slot.includes('XL1') && (
              <Badge variant="outline" className="border-sky-200 bg-sky-50 text-sky-700">
                transparent
              </Badge>
            )}
          </div>
        </TableCell>
        <TableCell>
          <div className="flex flex-wrap items-center gap-1">
            {gamOnly ? (
              <Badge variant="secondary">tylko GAM</Badge>
            ) : (
              <span className="font-mono text-xs text-slate-600">
                {bids.length} {bids.length === 1 ? 'bid' : 'bidów'}
              </span>
            )}
            {warn && (
              <Badge
                variant="outline"
                title={blueprintIssueLabels(issues).join(' · ')}
                className="border-amber-300 bg-amber-100 text-amber-800"
              >
                ⚠ ryzyko braku bidów
              </Badge>
            )}
          </div>
        </TableCell>
        <TableCell className="text-right">
          <div className="inline-flex items-center gap-0.5">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-7"
              disabled={!onMoveUp}
              onClick={stop(() => onMoveUp?.())}
              aria-label="Przesuń w górę"
            >
              <ArrowUp className="size-3.5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-7"
              disabled={!onMoveDown}
              onClick={stop(() => onMoveDown?.())}
              aria-label="Przesuń w dół"
            >
              <ArrowDown className="size-3.5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-7 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
              onClick={stop(onRemove)}
              aria-label="Usuń adUnit"
            >
              <Trash2 className="size-3.5" />
            </Button>
          </div>
        </TableCell>
      </TableRow>

      {open && (
        <TableRow className="bg-slate-50 hover:bg-slate-50">
          <TableCell colSpan={7} className="whitespace-normal px-4 pb-4 pt-0">
            <div className="space-y-4 rounded-md border border-slate-200 bg-white p-4">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                <Field label="Kod AdUnitu (wspólny)">
                  <input
                    value={unit.slot}
                    onChange={(e) => onChangeUnit({ ...unit, slot: e.target.value })}
                    className="h-9 rounded border border-slate-200 bg-white px-2 font-mono text-sm outline-none focus:border-slate-400"
                  />
                </Field>
                <Field label="Typ reklamy (wspólny)">
                  <select
                    value={unit.typeId}
                    onChange={(e) => changeType(e.target.value)}
                    className="h-9 rounded border border-slate-200 bg-white px-2 font-mono text-sm outline-none focus:border-slate-400"
                  >
                    {types.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.id} — {t.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label={`Aktywny (${draft.service})`}>
                  <div className="flex h-9 items-center gap-2">
                    <Switch
                      checked={active}
                      onCheckedChange={(v) => onChangeServiceBp({ active: v })}
                    />
                    <span className="text-xs text-slate-400">
                      wyłączony = brak slotu i placeholdera
                    </span>
                  </div>
                </Field>
                <Field label={`Tylko GAM (${draft.service})`}>
                  <div className="flex h-9 items-center gap-2">
                    <Switch
                      checked={gamOnly}
                      onCheckedChange={(v) => onChangeServiceBp({ gamOnly: v })}
                    />
                    <span className="text-xs text-slate-400">omija aukcję Prebid</span>
                  </div>
                </Field>
              </div>

              <div>
                <span className="mb-1 block text-[11px] uppercase tracking-wide text-slate-500">
                  Wysokość placeholdera (adHigh px)
                </span>
                <div className="grid grid-cols-3 gap-3 md:max-w-md">
                  {(['desktop', 'tablet', 'mobile'] as const).map((device) => (
                    <Field key={device} label={device}>
                      <input
                        type="number"
                        min={0}
                        value={unit.adHigh[device]}
                        onChange={(e) => setDeviceHigh(device, Number(e.target.value) || 0)}
                        className="rounded border border-slate-200 bg-white px-2 py-1 font-mono text-sm outline-none focus:border-slate-400"
                      />
                    </Field>
                  ))}
                </div>
              </div>

              <div>
                <span className="mb-1 block text-[11px] uppercase tracking-wide text-slate-500">
                  Rozmiary reklam (wspólne) — domyślnie z typu {unit.typeId}, można nadpisać per
                  urządzenie
                </span>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  {(['desktop', 'tablet', 'mobile'] as const).map((device) => {
                    const own = unit.sizes?.[device]
                    return (
                      <Field
                        key={device}
                        label={`${device} — ${own ? 'własne' : 'z typu'}`}
                      >
                        <SizesInput
                          value={own ?? type?.sizes[device] ?? []}
                          onChange={(next) => setDeviceSizes(device, next)}
                        />
                        {own && (
                          <button
                            type="button"
                            onClick={() => setDeviceSizes(device, null)}
                            className="self-start text-[11px] text-slate-500 underline hover:text-slate-800"
                          >
                            przywróć z typu
                          </button>
                        )}
                      </Field>
                    )
                  })}
                </div>
              </div>

              {gamOnly ? (
                <p className="rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500">
                  Slot oznaczony jako „Tylko GAM” — idzie bezpośrednio do Google Ad Managera z
                  rozmiarami typu <code>{unit.typeId}</code>, bez aukcji Prebid.
                </p>
              ) : (
                <SlotBidsList
                  bids={bids}
                  typeId={unit.typeId}
                  service={draft.service}
                  registry={draft.bidders ?? {}}
                  onChange={(next) => onChangeServiceBp({ bids: next })}
                  onCopyToType={() => onCopyBidsToType(bids)}
                />
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

function SlotBidsList({
  bids,
  typeId,
  service,
  registry,
  onChange,
  onCopyToType,
}: {
  bids: SlotBid[]
  typeId: string
  service: string
  registry: Record<string, Record<string, unknown>>
  onChange: (next: SlotBid[]) => void
  onCopyToType: () => void
}) {
  const known = Object.keys(registry)
  const missing = known.filter((b) => !bids.some((bid) => bid.bidder === b))

  function update(idx: number, next: SlotBid) {
    onChange(bids.map((b, i) => (i === idx ? next : b)))
  }
  function remove(idx: number) {
    onChange(bids.filter((_, i) => i !== idx))
  }
  function move(idx: number, dir: -1 | 1) {
    const target = idx + dir
    if (target < 0 || target >= bids.length) return
    const next = bids.slice()
    const tmp = next[idx]!
    next[idx] = next[target]!
    next[target] = tmp
    onChange(next)
  }

  return (
    <fieldset className="rounded border border-slate-200 p-3">
      <legend className="px-1 text-[11px] uppercase tracking-wide text-slate-600">
        Bidy Prebid w serwisie {service} ({bids.length})
      </legend>

      <div className="mb-2 flex justify-end">
        <Button type="button" variant="outline" size="sm" onClick={onCopyToType}>
          Zastosuj te bidy dla wszystkich AdUnitów typu {typeId} w {service}
        </Button>
      </div>

      <ul className="space-y-2">
        {bids.length === 0 && (
          <li className="text-xs text-slate-400">Brak bidów — slot nie weźmie udziału w aukcji.</li>
        )}
        {bids.map((bid, idx) => {
          const shared = registry[bid.bidder]
          const unknown = bid.bidder && !shared
          return (
            <li
              key={idx}
              className={
                'rounded border bg-white p-2 ' + (unknown ? 'border-amber-300' : 'border-slate-200')
              }
            >
              <div className="flex flex-wrap items-start gap-3">
                <div className="flex flex-1 flex-col gap-1">
                  <label className="text-[11px] uppercase tracking-wide text-slate-500">
                    dostawca
                  </label>
                  <input
                    list="slot-bidders-list"
                    value={bid.bidder}
                    onChange={(e) => update(idx, { ...bid, bidder: e.target.value })}
                    placeholder="np. teads"
                    className="rounded border border-slate-200 bg-white px-2 py-1 font-mono text-sm outline-none focus:border-slate-400"
                  />
                  {unknown ? (
                    <span className="text-[11px] text-amber-700">
                      spoza rejestru — nie dostanie wspólnych parametrów
                    </span>
                  ) : (
                    shared &&
                    Object.keys(shared).length > 0 && (
                      <span className="text-[11px] text-slate-400">
                        dziedziczy: {Object.keys(shared).join(', ')}
                      </span>
                    )
                  )}
                </div>
                <div className="flex flex-[2] flex-col gap-1">
                  <label className="text-[11px] uppercase tracking-wide text-slate-500">
                    params — tylko to, co per slot (JSON)
                  </label>
                  <JsonInline
                    value={bid.params ?? {}}
                    onChange={(p) => {
                      const params = p as Record<string, unknown>
                      if (!params || Object.keys(params).length === 0) {
                        const { params: _omit, ...rest } = bid
                        update(idx, rest)
                        return
                      }
                      update(idx, { ...bid, params })
                    }}
                  />
                </div>
                <div className="flex flex-col gap-1 pt-5">
                  <button
                    type="button"
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    className="rounded border border-slate-200 px-2 py-0.5 text-xs text-slate-600 hover:bg-slate-100 disabled:opacity-30"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => move(idx, 1)}
                    disabled={idx === bids.length - 1}
                    className="rounded border border-slate-200 px-2 py-0.5 text-xs text-slate-600 hover:bg-slate-100 disabled:opacity-30"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(idx)}
                    className="rounded border border-rose-200 px-2 py-0.5 text-xs text-rose-600 hover:bg-rose-50"
                  >
                    ✕
                  </button>
                </div>
              </div>
            </li>
          )
        })}
      </ul>

      <datalist id="slot-bidders-list">
        {known.map((b) => (
          <option key={b} value={b} />
        ))}
      </datalist>

      <div className="mt-3 flex flex-wrap items-center gap-1 border-t border-slate-100 pt-3">
        <span className="mr-1 text-xs text-slate-400">Dodaj dostawcę:</span>
        {missing.length === 0 && (
          <span className="text-xs text-slate-400">wszyscy z rejestru już są.</span>
        )}
        {missing.map((b) => (
          <button
            key={b}
            type="button"
            onClick={() => onChange([...bids, { bidder: b }])}
            className="rounded border border-slate-200 bg-slate-50 px-2 py-0.5 font-mono text-xs text-slate-600 hover:border-slate-300 hover:bg-slate-100"
          >
            + {b}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onChange([...bids, { bidder: '' }])}
          className="ml-auto rounded border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-600 hover:bg-slate-50"
        >
          + inny
        </button>
      </div>
    </fieldset>
  )
}
